import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const LINKS = [
  { id: "servizi", label: "Servizi", testid: "nav-link-services" },
  { id: "piani", label: "Piani", testid: "nav-link-pricing" },
  { id: "agevolazioni", label: "Agevolazioni", testid: "nav-link-aid" },
  { id: "contatti", label: "Contatti", testid: "nav-link-contact" },
];

export function scrollToSection(id) {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
  else el.scrollIntoView({ behavior: "smooth" });
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <motion.header
      initial={{ y: -70, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-white/80 border-b border-neutral-200/80"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-brand-logo" className="flex items-center gap-2 group">
          <img src="/lumora-logo.png" alt="Lumora" className="w-8 h-8" />
          <span className="font-display font-extrabold text-lg tracking-tight">
            LUMORA<span className="text-[#E05638]">.</span>
          </span>
          <span className="mono-label text-neutral-500 hidden sm:inline group-hover:text-[#E05638] transition-colors">
            Web Studio
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => scrollToSection(l.id)}
              className="text-sm font-medium text-neutral-600 hover:text-[#111111] transition-colors relative after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-[#E05638] hover:after:w-full after:transition-all after:duration-300"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/blog"
            data-testid="nav-link-blog"
            className="text-sm font-medium text-neutral-600 hover:text-[#111111] transition-colors relative after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-[#E05638] hover:after:w-full after:transition-all after:duration-300"
          >
            Blog
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-link-client-area"
            onClick={() => navigate(user ? "/dashboard" : "/accedi")}
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-[#111111] text-white text-sm font-medium px-5 py-2.5 hover:bg-[#E05638] transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:ring-offset-2"
          >
            Area Clienti
            <ArrowUpRight size={15} />
          </button>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="md:hidden p-2 text-neutral-700"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="md:hidden border-t border-neutral-200 bg-white/95 backdrop-blur-md px-6 py-4 flex flex-col gap-4">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-mobile-${l.id}`}
              onClick={() => {
                setOpen(false);
                scrollToSection(l.id);
              }}
              className="text-left text-base font-medium text-neutral-700"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-mobile-blog"
            onClick={() => {
              setOpen(false);
              navigate("/blog");
            }}
            className="text-left text-base font-medium text-neutral-700"
          >
            Blog
          </button>
          <button
            data-testid="nav-mobile-client-area"
            onClick={() => navigate(user ? "/dashboard" : "/accedi")}
            className="rounded-full bg-[#111111] text-white text-sm font-medium px-5 py-2.5 w-max"
          >
            Area Clienti
          </button>
        </nav>
      )}
    </motion.header>
  );
}
